/**
 * Support Page - Patient support request list
 *
 * Mobile-friendly list of support requests with navigation to detail/create pages.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import { supportService } from "../services/support";
import {
  relationshipService,
  type ClinicShare,
} from "../services/relationships";
import type { SupportRequest } from "../types/support";
import { PRIORITY_INFO, STATUS_INFO } from "../types/support";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "../components/app-shell";

@customElement("support-page")
export class SupportPage extends LitElement {
  @state() private requests: SupportRequest[] = [];
  @state() private clinicShares: ClinicShare[] = [];
  @state() private isLoading = true;
  @state() private error = "";

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 600px;
      margin: 0 auto;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
    }

    .header h1 {
      margin: 0;
      font-size: 1.5rem;
      color: #333;
    }

    .requests-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .request-card {
      background: #fff;
      border: 2px solid #e9ecef;
      border-radius: 0.75rem;
      padding: 1rem;
      cursor: pointer;
      transition: all 0.2s;
      border-left-width: 4px;
    }

    .request-card:hover {
      background: #f8f9fa;
      border-color: #dee2e6;
    }

    .request-card:active {
      transform: scale(0.99);
    }

    .request-card.priority-question {
      border-left-color: #2196f3;
    }

    .request-card.priority-help-this-week {
      border-left-color: #ff9800;
    }

    .request-card.priority-urgent {
      border-left-color: #f44336;
    }

    .request-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }

    .request-subject {
      font-weight: 600;
      color: #333;
      margin: 0;
      font-size: 1rem;
      flex: 1;
    }

    .request-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      font-size: 0.8125rem;
      color: #666;
      align-items: center;
    }

    .priority-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.125rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .meta-separator {
      color: #ccc;
    }

    .loading-container,
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      color: #666;
    }

    .empty-state wa-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: #ccc;
    }

    .empty-state p {
      margin: 0;
      line-height: 1.5;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._loadData();
  }

  private async _loadData() {
    try {
      this.isLoading = true;
      this.error = "";

      const [requests, clinicShares] = await Promise.all([
        supportService.getMyRequests(),
        relationshipService.getMyClinicShares(),
      ]);

      this.requests = requests;
      this.clinicShares = clinicShares;
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load data";
    } finally {
      this.isLoading = false;
    }
  }

  private _goToNewRequest() {
    Router.go("/support/new");
  }

  private _goToDetail(request: SupportRequest) {
    Router.go(`/support/request/${request.requestId}`);
  }

  private _formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
    });
  }

  private _getClinicName(clinicGroupId: string): string {
    const clinic = this.clinicShares.find(
      (cs) => cs.clinicId === clinicGroupId,
    );
    return clinic?.clinicName || "Clinic";
  }

  render() {
    return html`
      <app-shell pageTitle="Support">
        <div class="page-content">
          <div class="header">
            <h1>Support Requests</h1>
            ${this.clinicShares.length > 0
              ? html`
                  <wa-button variant="brand" @click=${this._goToNewRequest}>
                    <wa-icon slot="prefix" name="circle-plus"></wa-icon>
                    New Request
                  </wa-button>
                `
              : null}
          </div>

          ${this.error
            ? html`
                <wa-callout variant="danger" open>
                  <wa-icon slot="icon" name="octagon-exclamation"></wa-icon>
                  ${this.error}
                </wa-callout>
              `
            : null}
          ${this.isLoading
            ? html`
                <div class="loading-container">
                  <wa-spinner style="font-size: 2rem;"></wa-spinner>
                  <p>Loading...</p>
                </div>
              `
            : this.clinicShares.length === 0
              ? html`
                  <div class="empty-state">
                    <wa-icon name="building"></wa-icon>
                    <p>
                      You're not connected to any clinic yet.<br />
                      Connect with a clinic from the Share page to request
                      support.
                    </p>
                  </div>
                `
              : this.requests.length === 0
                ? html`
                    <div class="empty-state">
                      <wa-icon name="comment-dots"></wa-icon>
                      <p>
                        No support requests yet.<br />
                        Tap "New Request" to ask your clinic a question.
                      </p>
                    </div>
                  `
                : html`
                    <div class="requests-list">
                      ${this.requests.map((req) =>
                        this._renderRequestCard(req),
                      )}
                    </div>
                  `}
        </div>
      </app-shell>
    `;
  }

  private _renderRequestCard(request: SupportRequest) {
    const priorityInfo = PRIORITY_INFO[request.priority];
    const statusInfo = STATUS_INFO[request.status];

    return html`
      <div
        class="request-card priority-${request.priority}"
        @click=${() => this._goToDetail(request)}
      >
        <div class="request-header">
          <h3 class="request-subject">${request.subject}</h3>
          <wa-badge
            variant="${request.status === "resolved" ? "success" : "neutral"}"
          >
            ${statusInfo.label}
          </wa-badge>
        </div>
        <div class="request-meta">
          <span
            class="priority-badge"
            style="background: ${priorityInfo.color}22; color: ${priorityInfo.color}"
          >
            ${priorityInfo.label}
          </span>
          <span class="meta-separator">·</span>
          <span>${this._getClinicName(request.clinicGroupId)}</span>
          <span class="meta-separator">·</span>
          <span>${this._formatDate(request.createdAt)}</span>
          ${request.responseCount > 0
            ? html`
                <span class="meta-separator">·</span>
                <span
                  >${request.responseCount}
                  response${request.responseCount !== 1 ? "s" : ""}</span
                >
              `
            : null}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "support-page": SupportPage;
  }
}
